import {DashboardLayout, PrimaryButton, SecondaryButton} from 'components';
import React from 'react';
import StatusDistribution from './charts/StatusDistribution';
import IdentityVerificationChart from './charts/IdentityVerification';
import DocumentAnalysisChart from './charts/DocumentAnalysis';
import BiometricsChart from './charts/BioMetrics';
import MessagingChart from './charts/Messaging';
import FraudMonitoringChart from './charts/FraudMonitoring';
import BackgroundIntelligenceChart from './charts/BackgroundInteliigence';
import AddressVerification from './charts/AddressVerification';

const topCountries = [
  {
    country: 'Nigeria',
    verification_count: 150,
    percentage: 45,
    flag: 'https://flagpedia.net/data/flags/h80/ng.png',
  },
  {
    country: 'Ghana',
    verification_count: 85,
    percentage: 25,
    flag: 'https://flagpedia.net/data/flags/h80/gh.png',
  },
  {
    country: 'Kenya',
    verification_count: 50,
    percentage: 15,
    flag: 'https://flagpedia.net/data/flags/h80/ke.png',
  },
  {
    country: 'South Africa',
    verification_count: 35,
    percentage: 10,
    flag: 'https://flagpedia.net/data/flags/h80/za.png',
  },
  {
    country: 'United States',
    verification_count: 15,
    percentage: 5,
    flag: 'https://flagpedia.net/data/flags/h80/us.png',
  },
];

const topEndpoints = [
  {name: 'NIN', count: 1200},
  {name: 'BVN BASIC', count: 1000},
  {name: 'RESIDENTIAL PERMIT', count: 150},
  {name: 'PHONE CHECK', count: 98},
  {name: "DRIVER'S LICENSE", count: 72},
  {name: 'COMPANY SEARCH', count: 55},
  {name: "VOTER'S CARD", count: 41},
  {name: 'BANK STATEMENT', count: 23},
];

function ApiAnalysis() {
  return (
    <DashboardLayout
      leftMargin="!ml-6"
      xlRightPadding="!pr-6"
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">API Analysis</p>
      }
    >
      <div className="flex items-center py-2 flex-wrap gap-3 justify-end mx-[-32px] px-8 border border-transparent border-b-[#E4E3EC]">
        <div className="flex items-center gap-2 flex-wrap">
          <SecondaryButton buttonText="Filter" />
          <PrimaryButton buttonText="Export" />
        </div>
      </div>
      <div className="pt-3 grid xl:grid-cols-[37%_37%_22%] gap-6">
        <StatusDistribution />

        <div className="relative bg-white shadow-cards w-full h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
          <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl">
            <p className="font-medium text-sm xl:text-base text-deepBlue">
              Country Distribution
            </p>
          </div>

          <div className="flex items-start gap-4 px-6 py-4">
            <div className="flex gap-[15px] w-[48%] flex-col items-start">
              {topCountries.map((country, index) => (
                <div key={index} className="flex items-center gap-2 h-[24px]">
                  {country.flag ? (
                    <img
                      src={country.flag}
                      alt={country.country || 'country flag'}
                      className="min-w-[24px] min-h-[24px] rounded-full w-6 h-6 object-cover"
                    />
                  ) : (
                    <div className="min-w-[24px] min-h-[24px] w-6 h-6 rounded-full bg-[#E9EBF0] font-inter flex items-center justify-center text-batchBlue font-bold">
                      {country.country.charAt(0) || 'N/A'}
                    </div>
                  )}
                  <p className="text-[#464646] font-medium text-xs uppercase font-inter tracking-[-0.5px]">
                    {country.country || 'N/A'}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex items-start flex-col w-full gap-[15px]">
              {topCountries.map((country, index) => (
                <div key={index} className="w-full">
                  <div className="flex justify-between items-center h-[24px]">
                    <div className="w-[80%] h-[13px] bg-[#E9EBF0] rounded overflow-hidden">
                      <div
                        style={{width: `${country.percentage}%`}}
                        className="h-full bg-batchBlue rounded transition-all duration-200 ease-out"
                      />
                    </div>
                    <p className="text-[#858daa] font-semibold text-sm ml-2">
                      {country.percentage.toFixed(1) || 0}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:block hidden border border-transparent border-t-[#F2F2F2] h-5 absolute bottom-0 w-full" />
        </div>

        <div className="relative bg-white shadow-cards w-full h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
          <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl border-b-[0.5px] border-[#EFEFEF]">
            <p className="font-medium text-sm xl:text-base text-deepBlue">
              Top Endpoints
            </p>
          </div>

          <div className="h-[200px] overflow-y-auto scrollbar-visible px-6 pt-4 pb-2 space-y-6">
            {topEndpoints.map((endpoint, index) => (
              <div key={index} className="flex items-center justify-between">
                <p className="text-[#464646] font-medium text-xs uppercase font-inter tracking-[-0.5px]">
                  {endpoint.name}
                </p>
                <p className="text-[#080917] font-semibold text-sm">
                  {endpoint.count}
                </p>
              </div>
            ))}
          </div>

          <div className="md:block hidden border border-transparent border-t-[#F2F2F2] h-5 absolute bottom-0 w-full" />
        </div>
      </div>

      <IdentityVerificationChart />

      <DocumentAnalysisChart />

      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <BiometricsChart />

        <MessagingChart />
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <FraudMonitoringChart />

        <BackgroundIntelligenceChart />
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <AddressVerification />
      </div>
    </DashboardLayout>
  );
}

export default ApiAnalysis;
